import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';


const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Use Stripe publishable key from environment variable
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const normalizeWebsiteOrderItems = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item) => {
    const quantity = Number.isInteger(Number(item?.quantity)) && Number(item?.quantity) > 0
      ? Number(item.quantity)
      : 1;
    const ingredients = Array.isArray(item?.ingredients) && item.ingredients.length > 0
      ? item.ingredients
      : (item?.name ? [item.name] : []);
    const derivedType = item?.type || item?.menuType || (item?.length || item?.extras ? 'f' : 'sk');
    const size = item?.size || item?.length || (derivedType === 'sk' ? 'Small' : '');

    return {
      ...item,
      quantity,
      ingredients,
      type: derivedType,
      size,
    };
  });
};

function CheckoutForm({ amount, name, email, items, totalItems, totalPrice, salesTax, tip, finalTotal, totalPoints, status, orderType, pickupTime, eventDate, subtotal, location, deliveryFee, payloadOverrides, onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const trimmedName = String(name || '').trim();
  const trimmedEmail = String(email || '').trim();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const hasValidName = Boolean(trimmedName);
  const hasValidEmail = emailRegex.test(trimmedEmail);
  const isPayDisabled = !stripe || loading || !clientSecret || !hasValidName || !hasValidEmail;


  useEffect(() => {
    // Public website checkout always requires name and email before creating the intent.
    if (!hasValidName) return;
    if (!hasValidEmail) return;

    const normalizedOrderType = orderType === 'website' ? 'standard' : orderType;
    const normalizedItems = normalizedOrderType === 'catering'
      ? items
      : normalizeWebsiteOrderItems(items);
    const normalizedLocation = location || 'Pickup';
    const normalizedOrigin = 'website';
    const normalizedEmail = trimmedEmail;
    const nameParts = trimmedName.split(/\s+/).filter(Boolean);
    const firstName = nameParts[0] || trimmedName || 'guest';
    const lastName = nameParts.slice(1).join(' ');

    const requestBody = {
      amount,
      currency: 'usd',
      items: normalizedItems,
      totalItems,
      subtotal: subtotal ?? totalPrice,
      totalPrice,
      salesTax,
      tip,
      deliveryFee: deliveryFee ?? 0,
      finalTotal,
      totalPoints,
      status: status || 'pending',
      userEmail: normalizedEmail,
      email: normalizedEmail,
      firstName,
      lastName,
      authUid: '',
      userId: '',
      orderType: normalizedOrderType,
      origin: normalizedOrigin,
      location: normalizedLocation,
      pickupTime,
      eventDate,
      ...(payloadOverrides || {}),
    };

    const debounceTimer = setTimeout(() => {
    fetch('https://us-central1-mickeyssubs.cloudfunctions.net/createWebPaymentIntent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to initialize payment');
        return res.json();
      })
      .then((data) => setClientSecret(data.clientSecret))
      .catch((err) => setError('Failed to initialize payment.'));
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [amount, items, totalItems, totalPrice, salesTax, tip, finalTotal, totalPoints, status, orderType, pickupTime, eventDate, hasValidEmail, hasValidName, trimmedEmail, trimmedName, subtotal, location, deliveryFee, payloadOverrides]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    if (!stripe || !elements) return;
    const cardElement = elements.getElement(CardElement);
    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: name || undefined,
          email: email || undefined,
        },
      },
    });
    setLoading(false);
    if (error) {
      setError(error.message);
      onError && onError(error);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess && onSuccess(paymentIntent);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        maxWidth: '100%',
        margin: '0 auto',
        background: '#fafbfc',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
        padding: 24,
        border: '1px solid #e0e0e0',
      }}
    >
      <div style={{ width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
        <div
          style={{
            border: '1.5px solid #bfc9d1',
            borderRadius: 8,
            padding: '18px 16px',
            background: '#fff',
            marginBottom: 24,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <CardElement
            options={{
              hidePostalCode: true,
              style: {
                base: {
                  fontSize: '18px',
                  color: '#222',
                  
                  fontFamily: 'Montserrat, sans-serif',
                  '::placeholder': { color: '#bfc9d1' },
                },
                invalid: {
                  color: '#FF6B6B',
                },
              },
            }}
          />
        </div>
        <Button type="submit" variant="contained" disabled={isPayDisabled} style={{ width: '100%', marginTop: 0, borderRadius: 8, fontSize: 16, backgroundColor: isPayDisabled ? '#b8bec5' : '#3AC28E', color: isPayDisabled ? '#f5f5f5' : '#fff', textTransform: 'none', fontFamily: 'FontSemi, sans-serif', boxShadow: 'none', cursor: isPayDisabled ? 'not-allowed' : 'pointer' }}>
          {loading ? <CircularProgress size={22} style={{ color: 'white' }} /> : 'Pay'}
        </Button>
      </div>
      {error && <div style={{ color: 'red', marginTop: 10 }}>{error}</div>}
    </form>
  );
}

export default function ClientPaymentForm({ amount, name, email, items, totalItems, totalPrice, salesTax, tip, finalTotal, totalPoints, status, orderType, pickupTime, eventDate, subtotal, location, deliveryFee, payloadOverrides, onSuccess, onError }) {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm 
        amount={amount} 
        name={name} 
        email={email} 
        items={items}
        totalItems={totalItems}
        totalPrice={totalPrice}
        salesTax={salesTax}
        tip={tip}
        finalTotal={finalTotal}
        totalPoints={totalPoints}
        status={status}
        orderType={orderType}
        pickupTime={pickupTime}
        eventDate={eventDate}
        subtotal={subtotal}
        location={location}
        deliveryFee={deliveryFee}
        payloadOverrides={payloadOverrides}
        onSuccess={onSuccess} 
        onError={onError} 
      />
    </Elements>
  );
}
